import Phaser from "phaser";
import i18next from "i18next";
import { hideJoysticks, showJoysticks } from "../controls/controls-touch";
import { COLORS } from "./colors";
import { MatchEndData, MatchEndReason } from "../types/match-end-data";
import { trackEvent } from "../utils/ga-events";
import { isTouchDevice } from "../utils/mobile-utils";
import { playSound } from "../utils/sound-utils";
import { DailyStarsComponent } from "./daily-stars-component";
import { clearAllDamageTexts } from "./damage-text";
import { ModernButton } from "./modern-button";
import { PlayerData } from "../models/common";

export class EndScreen {
  private bg: Phaser.GameObjects.Image;
  private container: Phaser.GameObjects.Container;
  private respawnButton: ModernButton | null = null;
  private deathText: Phaser.GameObjects.Text;
  private reasonText: Phaser.GameObjects.Text;
  private killEventsTextObjects: Phaser.GameObjects.Text[] = [];
  private killEventsContainer?: Phaser.GameObjects.Container;
  private scene: Phaser.Scene;
  private activeTweens: Phaser.Tweens.Tween[] = [];
  private pendingDelayedCalls: Phaser.Time.TimerEvent[] = [];
  private matchEndReason: MatchEndReason | null = null;
  private playerData: PlayerData | null = null;
  private matchEndData: MatchEndData | null = null;
  private dailyStarsComponent: DailyStarsComponent | null = null;
  private contentContainer: Phaser.GameObjects.Container | null = null;
  private dailyStarsContainer: Phaser.GameObjects.Container | null = null;
  private starBorders: any[] = [];
  private onHideCallback: ((completeFn: () => void) => void) | null = null;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    const { width, height } = scene.scale;

    // Background image
    this.bg = scene.add.image(width / 2, height / 2, "death-bg");
    this.bg.setOrigin(0.5);

    // Scale to cover screen while maintaining aspect ratio (crop vertically if needed)
    const scaleX = width / this.bg.width;
    const scaleY = height / this.bg.height;
    const scale = Math.max(scaleX, scaleY);
    this.bg.setScale(scale);

    this.bg.setScrollFactor(0);

    // Create a container for the content
    const contentContainer = scene.add.container(width / 2, height / 2);
    contentContainer.setScrollFactor(0);
    this.contentContainer = contentContainer; // Store for use in show() method

    // Center: "YOU DIED" label
    this.deathText = scene.add
      .text(0, -120, i18next.t("scenes:endScreen.youDied"), {
        fontSize: "48px",
        color: COLORS.RED,
        fontFamily: "Anton",
        shadow: {
          offsetX: 0,
          offsetY: 6,
          color: "#000000",
          blur: 0,
          stroke: true,
          fill: true,
        },
      })
      .setOrigin(0.5);
    this.deathText.setScrollFactor(0);
    contentContainer.add(this.deathText);

    // Reason text (hidden - not needed in this layout)
    this.reasonText = scene.add
      .text(0, 0, "", {
        fontSize: "16px",
        color: COLORS.ORANGE,
        fontFamily: "Anton",
      })
      .setOrigin(0.5);
    this.reasonText.setScrollFactor(0);
    this.reasonText.setVisible(false);
    contentContainer.add(this.reasonText);

    // Daily stars will be created in show() method
    // Respawn button at bottom center
    this.respawnButton = new ModernButton({
      scene: scene,
      x: 0,
      y: height / 2 - 80,
      text: i18next.t("scenes:endScreen.returnToBase"),
      style: "success",
      onClick: () => {
        playSound(this.scene, "menu-click-sound");
        this.handleReturnToBase();
      },
    });
    this.respawnButton.setScrollFactor(0);
    contentContainer.add(this.respawnButton);

    this.container = scene.add.container(0, 0, [this.bg, contentContainer]);
    this.container.setDepth(Number.MAX_SAFE_INTEGER); // Ensure on top of everything
    this.container.setScrollFactor(0);
    this.container.setVisible(false);
  }

  setOnHideCallback(fn: (completeFn: () => void) => void) {
    this.onHideCallback = fn;
  }

  show(matchEndData: MatchEndData, playerData: PlayerData) {
    // Store the match end reason for use in hide()
    this.matchEndReason = matchEndData.reason;

    // Store player data for use in hide()
    this.playerData = playerData;

    // Store the full match end data for use in handleReturnToBase()
    this.matchEndData = matchEndData;

    // Clean up any existing tweens before showing
    this.activeTweens.forEach((tween) => {
      if (tween && tween.isActive()) {
        tween.stop();
      }
    });
    this.activeTweens = [];

    this.container.setVisible(true);

    // Update reason text and top label
    if (matchEndData.reason === MatchEndReason.EXTRACTION_SUCCESS) {
      this.bg.setTexture("success-bg");
      this.deathText?.setText(i18next.t("scenes:endScreen.successfulExtraction"));
      this.deathText.setColor(COLORS.YELLOW);
      this.reasonText.setText(
        i18next.t("scenes:endScreen.successMessage"),
      );
      playSound(this.scene, "extraction-success", null, 0.4);
    } else if (matchEndData.reason) {
      this.deathText.setColor(COLORS.RED);
      switch (matchEndData.reason) {
        case MatchEndReason.TIMEOUT:
          this.deathText?.setText(i18next.t("scenes:endScreen.extractionFailed"));
          this.reasonText.setText(
            i18next.t("scenes:endScreen.timeoutMessage"),
          );
          break;
        default:
          this.deathText?.setText(i18next.t("scenes:endScreen.youDied"));
          this.reasonText.setText(matchEndData.reason);
          break;
      }
      // Play extraction-failed sound for death
      if (
        this.scene.sound &&
        this.scene.cache.audio.exists("extraction-failed")
      ) {
        playSound(this.scene, "extraction-failed", null, 0.4);
      }
    } else {
      this.deathText.setColor(COLORS.RED);
      this.deathText?.setText(i18next.t("scenes:endScreen.youDied"));
      this.reasonText.setText("");
      // Play extraction-failed sound for death
      playSound(this.scene, "extraction-failed", null, 0.4);
    }

    trackEvent(
      "match-events",
      matchEndData.matchEvents.reduce<Record<string, number>>((acc, e) => {
        acc[e.reason] = (acc[e.reason] ?? 0) + e.amount;
        return acc;
      }, {}),
    );

    if (playerData.killStreak > 0) {
      trackEvent("player-kill-streak", { kills: playerData.killStreak });
    }

    // Update daily stars based on dailyExtractions count
    const dailyExtractions =
      typeof playerData.dailyExtractions === "number"
        ? playerData.dailyExtractions
        : 0;

    const { width, height } = this.scene.scale;
    const starsY = -height / 2 + 80;

    // Destroy old stars component if it exists
    if (this.dailyStarsComponent) {
      this.dailyStarsComponent.destroy();
      this.dailyStarsComponent = null;
    }

    // Determine if we should animate the newly filled star
    const shouldAnimateNewlyFilled =
      matchEndData.reason === MatchEndReason.EXTRACTION_SUCCESS;
    const newlyFilledIndex = shouldAnimateNewlyFilled
      ? dailyExtractions - 1
      : -1;

    // Create new stars component with appropriate animation
    this.dailyStarsComponent = new DailyStarsComponent({
      scene: this.scene,
      x: -width / 2 + 128,
      y: starsY,
      dailyExtractions: dailyExtractions,
      starSize: 24,
      starSpacing: 10,
      label: i18next.t("scenes:endScreen.dailyRewards"),
      labelFontSize: "14px",
      labelColor: COLORS.YELLOW,
      animateNewlyFilled: shouldAnimateNewlyFilled,
      newlyFilledIndex: newlyFilledIndex,
    });

    // Add the component to content container (same level as respawn button)
    this.contentContainer?.add(this.dailyStarsComponent.getContainer());

    // Display match events (pvp + season xp) together in a single info box
    const rightX = 150;
    const eventsY = starsY;
    const padding = 16;
    const colW = 160;

    // Remove previous event text objects
    if (this.killEventsTextObjects.length > 0) {
      for (const obj of this.killEventsTextObjects) {
        obj.destroy();
      }
      this.killEventsTextObjects = [];
    }

    // Destroy old container if it exists
    if (this.killEventsContainer) {
      this.killEventsContainer.destroy();
    }

    // Create a single container for both pvp and season xp events
    this.killEventsContainer = this.scene.add.container(rightX, eventsY);
    this.killEventsContainer.setScrollFactor(0);
    this.contentContainer?.add(this.killEventsContainer);

    let y = 0;

    // Add title
    const titleText = this.scene.add
      .text(0, y, i18next.t("scenes:endScreen.matchEvents"), {
        fontSize: "14px",
        color: COLORS.YELLOW,
        fontFamily: "Anton",
        align: "left",
      })
      .setOrigin(0, 0);
    this.killEventsTextObjects.push(titleText);
    this.killEventsContainer?.add(titleText);
    y += titleText.height + 8;

    // Pvp events grouped by reason with Pts info
    if (
      Array.isArray(matchEndData.matchEvents) &&
      matchEndData.matchEvents.length > 0
    ) {
      const grouped: Record<string, { count: number; total: number }> = {};
      for (const e of matchEndData.matchEvents) {
        if (!grouped[e.reason]) {
          grouped[e.reason] = { count: 0, total: 0 };
        }
        grouped[e.reason].count += 1;
        grouped[e.reason].total += e.amount;
      }

      const entries = Object.entries(grouped);
      let grandTotal = 0;
      for (const [reason, { count, total }] of entries) {
        grandTotal += total;
        const sign = total >= 0 ? "+" : "";
        const ptsColor = total >= 0 ? COLORS.GREEN : COLORS.RED;
        const prefix = count > 1 ? `${count}x ` : "";
        // Reason label
        const reasonObj = this.scene.add
          .text(0, y, `${prefix}${reason}`, {
            fontSize: "13px",
            color: COLORS.WHITE,
            fontFamily: "Anton",
            align: "left",
          })
          .setOrigin(0, 0);
        // Pts value right-aligned at a fixed column
        const ptsObj = this.scene.add
          .text(colW, y, i18next.t("scenes:endScreen.points", { points: `${sign}${total.toLocaleString()}` }), {
            fontSize: "13px",
            color: ptsColor,
            fontFamily: "Anton",
            align: "right",
          })
          .setOrigin(1, 0);
        this.killEventsTextObjects.push(reasonObj, ptsObj);
        this.killEventsContainer?.add(reasonObj);
        this.killEventsContainer?.add(ptsObj);
        y += reasonObj.height + 4;
      }

      // Divider line + total
      y += 4;
      const divider = this.scene.add.graphics();
      divider.lineStyle(1, 0xffffff, 0.3);
      divider.lineBetween(0, y, colW, y);
      this.killEventsContainer?.add(divider);
      y += 6;

      const totalSign = grandTotal >= 0 ? "+" : "";
      const totalColor = grandTotal >= 0 ? COLORS.GREEN : COLORS.RED;
      const totalLabelObj = this.scene.add
        .text(0, y, i18next.t("scenes:endScreen.total"), {
          fontSize: "13px",
          color: COLORS.YELLOW,
          fontFamily: "Anton",
        })
        .setOrigin(0, 0);
      const totalValueObj = this.scene.add
        .text(colW, y, i18next.t("scenes:endScreen.points", { points: `${totalSign}${grandTotal.toLocaleString()}` }), {
          fontSize: "13px",
          color: totalColor,
          fontFamily: "Anton",
        })
        .setOrigin(1, 0);
      this.killEventsTextObjects.push(totalLabelObj, totalValueObj);
      this.killEventsContainer?.add(totalLabelObj);
      this.killEventsContainer?.add(totalValueObj);
      y += totalLabelObj.height + 4;

      // Add kill streak display with icon
      y += 8; // Spacing before kill streak
      const killStreakEmoji =
        this.matchEndReason === MatchEndReason.EXTRACTION_SUCCESS ? "🔥" : "💀";
      const killStreakColor =
        this.matchEndReason === MatchEndReason.EXTRACTION_SUCCESS
          ? COLORS.YELLOW
          : COLORS.GRAY;
      const killStreakText = this.scene.add
        .text(0, y, i18next.t("scenes:endScreen.killStreak", { emoji: killStreakEmoji, count: playerData.killStreak }), {
          fontSize: "14px",
          color: killStreakColor,
          fontFamily: "Anton",
          align: "left",
        })
        .setOrigin(0, 0);
      this.killEventsTextObjects.push(killStreakText);
      this.killEventsContainer?.add(killStreakText);
      y += killStreakText.height;
    } else {
      // No pvp events this match - show empty-state text instead of nothing
      const emptyText = this.scene.add
        .text(0, y, i18next.t("scenes:endScreen.noPvpRewards"), {
          fontSize: "13px",
          color: COLORS.WHITE,
          fontFamily: "Anton",
          align: "left",
        })
        .setOrigin(0, 0);
      this.killEventsTextObjects.push(emptyText);
      this.killEventsContainer?.add(emptyText);
      y += emptyText.height;
    }

    // Season xp events, appended below the pvp section in the same box
    const seasonXpEvents = matchEndData.seasonXpEvents;
    if (Array.isArray(seasonXpEvents) && seasonXpEvents.length > 0) {
      y += 14; // Spacing before the season xp section

      const xpTitleText = this.scene.add
        .text(0, y, i18next.t("scenes:endScreen.seasonXp"), {
          fontSize: "14px",
          color: COLORS.YELLOW,
          fontFamily: "Anton",
          align: "left",
        })
        .setOrigin(0, 0);
      this.killEventsTextObjects.push(xpTitleText);
      this.killEventsContainer?.add(xpTitleText);
      y += xpTitleText.height + 8;

      const grouped: Record<string, { count: number; total: number }> = {};
      for (const e of seasonXpEvents) {
        if (!grouped[e.reason]) {
          grouped[e.reason] = { count: 0, total: 0 };
        }
        grouped[e.reason].count += 1;
        grouped[e.reason].total += e.amount;
      }

      const entries = Object.entries(grouped);
      let grandTotal = 0;
      for (const [reason, { count, total }] of entries) {
        grandTotal += total;
        const prefix = count > 1 ? `${count}x ` : "";
        const reasonObj = this.scene.add
          .text(0, y, `${prefix}${reason}`, {
            fontSize: "13px",
            color: COLORS.WHITE,
            fontFamily: "Anton",
            align: "left",
          })
          .setOrigin(0, 0);
        const xpObj = this.scene.add
          .text(colW, y, i18next.t("scenes:endScreen.seasonXpAmount", { amount: total }), {
            fontSize: "13px",
            color: COLORS.GREEN,
            fontFamily: "Anton",
            align: "right",
          })
          .setOrigin(1, 0);
        this.killEventsTextObjects.push(reasonObj, xpObj);
        this.killEventsContainer?.add(reasonObj);
        this.killEventsContainer?.add(xpObj);
        y += reasonObj.height + 4;
      }

      y += 4;
      const xpDivider = this.scene.add.graphics();
      xpDivider.lineStyle(1, 0xffffff, 0.3);
      xpDivider.lineBetween(0, y, colW, y);
      this.killEventsContainer?.add(xpDivider);
      y += 6;

      const totalLabelObj = this.scene.add
        .text(0, y, i18next.t("scenes:endScreen.total"), {
          fontSize: "13px",
          color: COLORS.YELLOW,
          fontFamily: "Anton",
        })
        .setOrigin(0, 0);
      const totalValueObj = this.scene.add
        .text(colW, y, i18next.t("scenes:endScreen.seasonXpAmount", { amount: grandTotal }), {
          fontSize: "13px",
          color: COLORS.GREEN,
          fontFamily: "Anton",
        })
        .setOrigin(1, 0);
      this.killEventsTextObjects.push(totalLabelObj, totalValueObj);
      this.killEventsContainer?.add(totalLabelObj);
      this.killEventsContainer?.add(totalValueObj);
      y += totalLabelObj.height + 4;
    }

    // Single background panel sized to cover both sections
    const bgWidth = colW;
    const bgHeight = Math.max(y + 8, 40);
    const bgGraphics = this.scene.add.graphics();
    bgGraphics.fillStyle(0x000000, 0.5);
    bgGraphics.fillRoundedRect(
      -padding,
      -padding,
      bgWidth + padding * 2,
      bgHeight + padding * 2,
      8,
    );
    bgGraphics.setScrollFactor(0);
    bgGraphics.setDepth(100009);
    this.killEventsContainer?.add(bgGraphics);
    this.killEventsContainer?.sendToBack(bgGraphics);

    // Hide joysticks on mobile when death screen is shown
    if (isTouchDevice()) {
      hideJoysticks();
    }
  }

  /**
   * Draw the right side background panel with rounded corners
   */
  private handleReturnToBase() {
    // Check if we should show a daily reward screen
    // ONLY show rewards for successful extractions, not for deaths/timeouts
    if (
      this.playerData &&
      this.matchEndReason === MatchEndReason.EXTRACTION_SUCCESS
    ) {
      // Server has already determined if a reward should be shown (only on milestones 1, 3, 5)
      // and sent the dailyRewardMilestone field in matchEndData
      if (
        this.matchEndData &&
        this.matchEndData.dailyRewardMilestone !== undefined
      ) {
        const dailyRewardMilestone = this.matchEndData.dailyRewardMilestone;

        // Get the dailyRewardScreen from the base scene
        const baseScene = this.scene.scene.get("BaseScene") as any;

        if (baseScene && baseScene.dailyRewardScreen) {
          if (this.container) {
            this.container.setVisible(false);
          }
          baseScene.dailyRewardScreen.show({
            showWheel: true,
            spinNumber: dailyRewardMilestone, // 1, 3, or 5
            onClose: () => {
              // Reward was already applied and saved when the screen was shown
              // Now just hide the end screen
              this.hide();
            },
          });
          return;
        } else {
          console.warn("Daily reward screen not found!");
        }
      } else {
        console.info(
          "No reward milestone from server (either not a milestone or max daily rewards reached)",
        );
      }
    } else {
      console.info("No playerData or not a successful extraction");
    }

    console.info("No reward screen to show, just hiding");
    // No reward screen to show, just hide
    this.hide();
  }

  hide() {
    // Stop all active tweens before destroying objects
    this.activeTweens.forEach((tween) => {
      if (tween && tween.isActive()) {
        tween.stop();
      }
    });
    this.activeTweens = [];

    // Cancel all pending delayed calls
    this.pendingDelayedCalls.forEach((delayedCall) => {
      if (delayedCall && delayedCall.callback) {
        this.scene.time.removeEvent(delayedCall);
      }
    });
    this.pendingDelayedCalls = [];

    // Clean up all active status labels (spawned XP notifications)
    clearAllDamageTexts();

    // Destroy kill events text objects
    if (this.killEventsTextObjects.length > 0) {
      for (const obj of this.killEventsTextObjects) {
        if (obj && obj.scene) {
          obj.destroy();
        }
      }
      this.killEventsTextObjects = [];
    }

    // Destroy kill events container
    if (this.killEventsContainer && this.killEventsContainer.scene) {
      this.killEventsContainer.destroy();
      this.killEventsContainer = undefined;
    }

    // Destroy daily stars container
    if (this.dailyStarsContainer && this.dailyStarsContainer.scene) {
      this.dailyStarsContainer.destroy();
      this.dailyStarsContainer = null;
    }

    // Destroy star borders
    this.starBorders.forEach((border) => {
      if (border && border.scene) {
        border.destroy();
      }
    });
    this.starBorders = [];

    // Destroy daily stars component
    if (this.dailyStarsComponent) {
      this.dailyStarsComponent.destroy();
      this.dailyStarsComponent = null;
    }

    // Destroy the main container and all its children
    if (this.container && this.container.scene) {
      this.container.destroy();
    }

    // Restore UI buttons when EndScreen hides
    const baseScene = this.scene as any;
    if (baseScene && baseScene.windowManager) {
      baseScene.windowManager.showAllAfterReward();
    }

    // Base ambience/music is deferred until the death/end screen is dismissed
    // (see base-scene.ts create()) so it never plays underneath it.
    if (baseScene && typeof baseScene.startBaseAmbience === "function") {
      baseScene.startBaseAmbience();
    }

    // Show joysticks on mobile when end screen hides
    if (isTouchDevice()) {
      showJoysticks();
    }

    // If an onHide callback is provided (e.g. XP gain widget), run it first and
    // show post-match tutorials only after the callback signals completion.
    if (this.onHideCallback) {
      this.onHideCallback(() => this.showPostMatchTutorials());
    } else {
      this.showPostMatchTutorials();
    }
  }

  private showPostMatchTutorials() {
    // Tutorials are disabled in game logic.
  }

  isVisible(): boolean {
    return this.container.visible;
  }
}
